import React, { useCallback, useEffect, useRef, useState } from 'react';
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from 'lucide-react';
import { useLightboxKeyboard } from '../hooks/useLightboxKeyboard';

/**
 * Fullscreen image lightbox with:
 *  - swipe left/right to navigate between images (when not zoomed)
 *  - double-tap / double-click to toggle 2x zoom
 *  - pan when zoomed (drag with mouse or finger)
 *  - keyboard: Esc to close, arrows to navigate, +/- to zoom
 *
 *  Props:
 *   images: [{ src, caption }]
 *   startIndex: number
 *   lang: 'it' | 'en'
 *   onClose: () => void
 */
const Lightbox = ({ images, startIndex = 0, lang = 'it', onClose }) => {
  const [index, setIndex] = useState(startIndex);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragRef = useRef({ dragging: false, startX: 0, startY: 0, originX: 0, originY: 0, moved: false });
  const lastTapRef = useRef(0);
  const containerRef = useRef(null);

  const total = images.length;
  const current = images[index] || images[0];

  const resetZoom = useCallback(() => { setScale(1); setOffset({ x: 0, y: 0 }); }, []);

  // Reset zoom when the user navigates to a different image
  useEffect(() => { resetZoom(); }, [index, resetZoom]);

  useLightboxKeyboard({
    onClose,
    onNext: useCallback(() => setIndex((i) => (i + 1) % total), [total]),
    onPrev: useCallback(() => setIndex((i) => (i - 1 + total) % total), [total]),
    onZoomIn: useCallback(() => setScale((s) => Math.min(3, s + 0.25)), []),
    onZoomOut: useCallback(() => setScale((s) => Math.max(1, s - 0.25)), []),
    onReset: resetZoom,
  });

  const goPrev = (e) => { e?.stopPropagation(); setIndex((i) => (i - 1 + total) % total); };
  const goNext = (e) => { e?.stopPropagation(); setIndex((i) => (i + 1) % total); };

  // Pointer handlers for swipe & pan
  const onPointerDown = (e) => {
    dragRef.current = {
      dragging: true,
      startX: e.clientX,
      startY: e.clientY,
      originX: offset.x,
      originY: offset.y,
      moved: false,
      startTime: Date.now(),
    };
    if (e.target.setPointerCapture) {
      try { e.target.setPointerCapture(e.pointerId); } catch (err) {
        // Safari may reject capture on non-element targets; log at debug level.
        if (process.env.NODE_ENV !== 'production') console.debug('setPointerCapture rejected:', err);
      }
    }
  };
  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d.dragging) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (Math.abs(dx) > 6 || Math.abs(dy) > 6) d.moved = true;
    if (scale > 1) {
      setOffset({ x: d.originX + dx, y: d.originY + dy });
    }
  };
  const onPointerUp = (e) => {
    const d = dragRef.current;
    if (!d.dragging) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    const elapsed = Date.now() - (d.startTime || Date.now());
    dragRef.current.dragging = false;

    // Swipe detection (only when NOT zoomed)
    if (scale === 1 && Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.2 && elapsed < 600) {
      if (dx < 0) goNext(); else goPrev();
      return;
    }

    // Double-tap detection
    if (!d.moved) {
      const now = Date.now();
      if (now - lastTapRef.current < 320) {
        // Toggle zoom
        if (scale === 1) {
          setScale(2);
          setOffset({ x: 0, y: 0 });
        } else {
          resetZoom();
        }
        lastTapRef.current = 0;
      } else {
        lastTapRef.current = now;
      }
    }
  };

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm flex items-center justify-center"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-label={current?.caption}
    >
      {/* Top bar */}
      <div className="absolute top-0 inset-x-0 flex items-center justify-between p-3 z-10 pointer-events-none">
        <div className="text-white/80 font-bold text-sm bg-white/10 rounded-full px-3 py-1 pointer-events-auto">
          {index + 1} / {total}
        </div>
        <div className="flex gap-2 pointer-events-auto">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); if (scale === 1) { setScale(2); } else { resetZoom(); } }}
            className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition"
            aria-label={scale === 1 ? 'Zoom in' : 'Reset zoom'}
          >
            {scale === 1 ? <ZoomIn size={20} /> : <ZoomOut size={20} />}
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition"
            aria-label={lang === 'it' ? 'Chiudi' : 'Close'}
          >
            <X size={22} />
          </button>
        </div>
      </div>

      {/* Prev / Next arrows (desktop only) */}
      {total > 1 && (
        <>
          <button
            type="button"
            onClick={goPrev}
            className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white items-center justify-center transition"
            aria-label="Previous"
          >
            <ChevronLeft size={28} />
          </button>
          <button
            type="button"
            onClick={goNext}
            className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white items-center justify-center transition"
            aria-label="Next"
          >
            <ChevronRight size={28} />
          </button>
        </>
      )}

      {/* Image stage */}
      <div
        className="relative w-full h-full flex items-center justify-center select-none"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        style={{ touchAction: scale > 1 ? 'none' : 'pan-y', cursor: scale > 1 ? 'grab' : 'zoom-in' }}
      >
        <img
          key={current?.src}
          src={current?.src}
          alt={current?.caption}
          draggable={false}
          className="max-w-[94vw] max-h-[82vh] object-contain pointer-events-none transition-transform duration-200"
          style={{ transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})` }}
        />
      </div>

      {/* Caption + hint */}
      <div className="absolute bottom-0 inset-x-0 pb-6 pt-10 bg-gradient-to-t from-black/80 to-transparent pointer-events-none">
        <figcaption translate="no" className="notranslate text-center text-white font-display text-lg md:text-2xl px-6">
          {current?.caption}
        </figcaption>
        {total > 1 && (
          <p className="mt-2 text-center text-white/50 text-xs">
            {lang === 'it' ? 'Scorri ← → per cambiare foto • doppio tap per zoom' : 'Swipe ← → to change • double tap to zoom'}
          </p>
        )}
      </div>
    </div>
  );
};

export default Lightbox;
